import { makeVar, useApolloClient, useReactiveVar } from '@apollo/client'
import { BucketMedia, BucketMediaByAlbumIdDocument, BucketMediaByAlbumIdQuery, MediaSource } from '../../../graphql/generated'
import { MediaData } from '../utils/sharedTypes'
import useBucketGallerySettings from './useBucketGallerySettings'

export type Status = 'IN_PROGRESS' | 'COMPLETE' | 'ERROR' | 'QUEUED' | number
export const uploadsInProgressVar = makeVar<Map<string, Status>>(new Map())

export default function useUploadsInProgress() {
  const client = useApolloClient()
  const uploadsInProgress = useReactiveVar(uploadsInProgressVar)
  const { settings } = useBucketGallerySettings()
  const { filterBy, sortBy } = settings

  const updateStatus = (id: string, status: Status) => {
    uploadsInProgress.set(id, status)
  }

  const clearUploadsInProgress = () => {
    uploadsInProgressVar(new Map())
  }

  const updateMediaItems = (current: BucketMediaByAlbumIdQuery, id: string, media: BucketMedia) => {
    const originalBucketItems = current.bucketMediaByAlbumId?.items ?? []
    const indexToUpdate = originalBucketItems.findIndex((item) => item.id === id)
    let updatedBucketItems = [...originalBucketItems]
    if (indexToUpdate !== -1) {
      updatedBucketItems[indexToUpdate] = media
    } else {
      updatedBucketItems = [media, ...originalBucketItems]
    }
    return {
      bucketMediaByAlbumId: {
        ...current.bucketMediaByAlbumId,
        items: updatedBucketItems
      }
    }
  }

  const updateBucketMediaCache = (bucketId: string, id: string, data: MediaData) => {
    const media: BucketMedia = {
      bucketId,
      id,
      ...data,
      source: MediaSource.ManualUpload,
      isRestricted: false,
      isFavorite: data.isFavorite ?? false,
      filename: data.filename ?? '',
      height: data.height ?? null,
      width: data.width ?? null,
      albumId: data.albumId ?? null,
      __typename: 'BucketMedia'
    }
    // Update the cached id to the id from the media service when available
    media.id = data.id ?? id
    const { albumId } = media

    client.cache.updateQuery({ query: BucketMediaByAlbumIdDocument, variables: { bucketId, filterBy, sortBy } }, (current) => {
      if (!current?.bucketMediaByAlbumId) return null
      return updateMediaItems(current, id, media)
    })

    if (!albumId) {
      return
    }

    client.cache.updateQuery({ query: BucketMediaByAlbumIdDocument, variables: { bucketId, albumId, filterBy, sortBy } }, (current) => {
      if (!current?.bucketMediaByAlbumId) return null
      return updateMediaItems(current, id, media)
    })
  }

  const beginTrackingProgress = (bucketId: string, id: string, data: MediaData) => {
    uploadsInProgress.set(id, 'IN_PROGRESS')
    // Update cache with blob image uri
    updateBucketMediaCache(bucketId, id, data)
  }

  const beginTrackingQueued = (bucketId: string, id: string, data: MediaData) => {
    uploadsInProgress.set(id, 'QUEUED')
    // Update cache with blank image uri
    updateBucketMediaCache(bucketId, id, data)
  }

  const markUploadComplete = async (bucketId: string, id: string, data: MediaData) => {
    updateStatus(id, 'COMPLETE')
    // Update cache with server image uri
    updateBucketMediaCache(bucketId, id, data)
  }

  const endTrackingProgress = (id: string) => {
    uploadsInProgress.delete(id)
  }

  return {
    beginTrackingProgress,
    beginTrackingQueued,
    clearUploadsInProgress,
    uploadsInProgress,
    updateStatus,
    updateBucketMediaCache,
    endTrackingProgress,
    markUploadComplete
  }
}
